<script setup lang="ts">
import {computed} from "vue";
import {TeamAbsence} from "@/core/interfaces/TeamPlanning";
import {
    ABSENCE_STATUS_BADGES,
    formatAbsenceDuration,
    formatAbsenceRange,
    motifOf
} from "@/planning/lib/absences";
import {CalendarOff} from "@lucide/vue";

const {absences, memberId} = defineProps<{
    absences: TeamAbsence[];
    memberId: number;
}>();

const memberAbsences = computed(() =>
    absences
        .filter(absence => absence.user_access_point_id === memberId)
        .sort((a, b) => b.starts_at.localeCompare(a.starts_at))
);
</script>

<template>
  <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div class="mb-3 flex items-center gap-2">
      <CalendarOff class="size-4.5 text-primary" />
      <span class="text-sm font-semibold text-gray-800">Historique des absences</span>
      <span class="text-xs text-gray-400">{{ memberAbsences.length }}</span>
    </div>
    <div v-if="!memberAbsences.length" class="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-400">
      Aucune absence enregistrée pour ce membre.
    </div>
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="absence in memberAbsences"
        :key="absence.id"
        class="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2.5"
      >
        <div class="min-w-0 flex-1">
          <!-- The reason verbatim, like the absences list, tinted by its
               catalog motive: a free-text one falls back on "Autre"'s tint. -->
          <span
            class="inline-flex max-w-full truncate rounded-full px-2 py-0.5 text-[11px] font-semibold"
            :class="motifOf(absence.reason).classes"
          >
            {{ absence.reason }}
          </span>
          <div class="mt-0.5 text-[11px] text-gray-400">
            {{ formatAbsenceRange(absence) }} · {{ formatAbsenceDuration(absence) }}
          </div>
        </div>
        <!-- Every absence saved from the counter is granted, so only the rows
             left by the former request flow are worth a badge. -->
        <span
          v-if="ABSENCE_STATUS_BADGES[absence.status]"
          class="inline-flex flex-none items-center rounded-md px-2 py-0.5 text-[10px] font-bold"
          :class="ABSENCE_STATUS_BADGES[absence.status]!.classes"
        >
          {{ ABSENCE_STATUS_BADGES[absence.status]!.label }}
        </span>
      </div>
    </div>
  </div>
</template>
